// Apolo IDE — .apollo/permissions.json loader (S4 · E3-WS1-T01).
//
// Reads the project's auto-run policy (allow/block in natural language) from
// `.apollo/permissions.json`. Pure parse/validate over an INJECTED reader (the
// file read happens at the edge — electron files.cjs — same as steering). The
// rel-path is a FIXED constant (no user input), so there is no traversal surface.
//
// LAW (risk #5): missing / malformed / unreadable → DEFAULT (deny-by-default).
// Never throws.

import { validatePermissions } from './s4Contracts.js';

/** Fixed, project-relative path (no user input → no traversal surface).
 * E9-T02 — convenção UNIFICADA: canônico `.apolo/` (onde já vivem features.json,
 * progress.md, specs); `.apollo/` (double-L) segue lido como LEGADO. */
export const PERMISSIONS_REL_PATH = '.apolo/permissions.json';
export const PERMISSIONS_LEGACY_REL_PATH = '.apollo/permissions.json';

/** A fresh, mutable deny-by-default policy (DEFAULT_PERMISSIONS is frozen). */
function freshDefault() {
  return { autoRun: { allow_instructions: [], block_instructions: [] } };
}

/**
 * Parse + validate permissions.json text. Malformed → deny-by-default.
 * @param {unknown} jsonText
 * @returns {{ ok: boolean, error: string|null, value: import('./s4Contracts.js').Permissions }}
 */
export function parsePermissions(jsonText) {
  if (typeof jsonText !== 'string' || !jsonText.trim()) {
    return { ok: false, error: 'empty permissions → deny-by-default', value: freshDefault() };
  }
  let parsed;
  try {
    parsed = JSON.parse(jsonText);
  } catch (e) {
    return { ok: false, error: `invalid JSON → deny-by-default: ${String(e?.message ?? e)}`, value: freshDefault() };
  }
  return validatePermissions(parsed);
}

/**
 * Load + validate `.apollo/permissions.json` from a session root via an injected
 * reader. Missing file → DEFAULT (deny-by-default), never throws.
 *
 * @param {(root:string, relPath:string) => Promise<string|null>|string|null} readFile
 * @param {string} root
 * @returns {Promise<{ ok: boolean, error: string|null, value: import('./s4Contracts.js').Permissions, absent?: boolean }>}
 */
export async function loadPermissions(readFile, root) {
  if (typeof readFile !== 'function') {
    return { ok: false, error: 'no reader → deny-by-default', value: freshDefault() };
  }
  let text;
  try {
    text = await readFile(root, PERMISSIONS_REL_PATH);
    // E9-T02 — fallback LEGADO: projeto antigo com .apollo/permissions.json
    // continua funcionando; o canônico .apolo/ vence quando os dois existem.
    if (text == null) text = await readFile(root, PERMISSIONS_LEGACY_REL_PATH);
  } catch (e) {
    return { ok: false, error: `read failed → deny-by-default: ${String(e?.message ?? e)}`, value: freshDefault() };
  }
  if (text == null) {
    // No policy file at all → deny-by-default (an explicit, expected state).
    return { ok: true, error: null, value: freshDefault(), absent: true };
  }
  return parsePermissions(text);
}
